import { type SiteMasterCredentialSearchOption } from "~hooks/useSiteMasterCredentialsCatalog";

export interface PlatformItemCount {
  count: number;
  name: string;
  type: "skill" | "certification";
}

export interface PlatformRankInfo {
  count: number;
  rank: number;
}

export interface ProfileQualification {
  name: string;
  type: "skill" | "certification";
  userId: string;
}

interface NamedQualification {
  name?: string | null;
  userId?: string | null;
}

function buildCredentialCatalogMaps(credentialCatalog: SiteMasterCredentialSearchOption[] | undefined) {
  const byCode = new Map<string, SiteMasterCredentialSearchOption>();
  const byTitle = new Map<string, SiteMasterCredentialSearchOption>();

  for (const item of credentialCatalog ?? []) {
    if (item.code) {
      byCode.set(item.code.toLowerCase(), item);
    }
    byTitle.set(item.title.toLowerCase(), item);
  }

  return { byCode, byTitle };
}

/** Resume certifications are stored as "CODE | Title" or "Title (CODE)". */
function certificationNameKeys(name: string): string[] {
  const keys = new Set<string>();
  const trimmed = name.trim();
  if (!trimmed) {
    return [];
  }

  keys.add(trimmed);

  const pipeIndex = trimmed.indexOf(" | ");
  if (pipeIndex >= 0) {
    const code = trimmed.slice(0, pipeIndex).trim();
    const title = trimmed.slice(pipeIndex + 3).trim();
    if (code) {
      keys.add(code);
    }
    if (title) {
      keys.add(title);
    }
  }

  const parenthetical = trimmed.match(/^(.*)\(([^)]+)\)\s*$/);
  if (parenthetical) {
    const title = parenthetical[1]?.trim();
    const code = parenthetical[2]?.trim();
    if (title) {
      keys.add(title);
    }
    if (code) {
      keys.add(code);
    }
  }

  return [...keys];
}

function searchableTexts(
  name: string,
  type: ProfileQualification["type"],
  catalogMaps: ReturnType<typeof buildCredentialCatalogMaps>,
): string[] {
  const texts = new Set<string>();
  const add = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed) {
      texts.add(trimmed);
    }
  };

  add(name);
  if (type !== "certification") {
    return [...texts];
  }

  for (const key of certificationNameKeys(name)) {
    add(key);
  }

  const catalogMatch = certificationNameKeys(name)
    .map((key) => catalogMaps.byCode.get(key.toLowerCase()) ?? catalogMaps.byTitle.get(key.toLowerCase()))
    .find(Boolean);

  if (catalogMatch?.code) {
    add(catalogMatch.code);
  }
  if (catalogMatch?.title) {
    add(catalogMatch.title);
  }

  return [...texts];
}

function containsSearch(texts: string[], keyword: string): boolean {
  const needle = keyword.trim().toLowerCase();
  if (!needle) {
    return false;
  }
  return texts.some((text) => text.includes(needle));
}

function matchesSearchType(itemType: ProfileQualification["type"], searchType: "skill" | "certification" | "keyword") {
  return searchType === "keyword" || itemType === searchType;
}

function readUserId(row: object, fallback: string): string {
  const value = (row as { userId?: unknown }).userId;
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

/** Keeps one row per profile entry. Rows without a user id stay separate until the API supplies one. */
export function profileQualificationsFromStats(
  skills: NamedQualification[] | null | undefined,
  certifications: NamedQualification[] | null | undefined,
): ProfileQualification[] {
  const qualifications: ProfileQualification[] = [];

  for (const skill of skills ?? []) {
    if (!skill.name?.trim()) {
      continue;
    }
    qualifications.push({
      name: skill.name,
      type: "skill",
      userId: readUserId(skill, `skill-row-${qualifications.length}`),
    });
  }

  for (const certification of certifications ?? []) {
    if (!certification.name?.trim()) {
      continue;
    }
    qualifications.push({
      name: certification.name,
      type: "certification",
      userId: readUserId(certification, `certification-row-${qualifications.length}`),
    });
  }

  return qualifications;
}

/** Counts distinct students whose skill or certification text contains the search, and ranks the closest platform item. */
export function findPlatformMatch(args: {
  credentialCatalog?: SiteMasterCredentialSearchOption[];
  keyword: string;
  platformItems: PlatformItemCount[];
  qualifications: ProfileQualification[];
  type: "skill" | "certification" | "keyword";
}): PlatformRankInfo | null {
  const catalogMaps = buildCredentialCatalogMaps(args.credentialCatalog);
  const matchingStudents = new Set<string>();

  for (const qualification of args.qualifications) {
    if (!matchesSearchType(qualification.type, args.type)) {
      continue;
    }
    if (containsSearch(searchableTexts(qualification.name, qualification.type, catalogMaps), args.keyword)) {
      matchingStudents.add(qualification.userId);
    }
  }

  if (matchingStudents.size === 0) {
    return null;
  }

  const matchingRanks = args.platformItems.flatMap((item, index) => {
    if (!matchesSearchType(item.type, args.type)) {
      return [];
    }
    return containsSearch(searchableTexts(item.name, item.type, catalogMaps), args.keyword) ? [index + 1] : [];
  });

  return {
    count: matchingStudents.size,
    rank: matchingRanks.length > 0 ? Math.min(...matchingRanks) : 1,
  };
}
